import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const SiteTasks = ({ site }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  
  // Состояние для данных
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [limit] = useState(20); // 20 задач на страницу
  
  // Функции для работы с датами
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(startOfMonth),
      to: formatDate(endOfMonth)
    };
  };

  // Состояние для фильтров
  const [filters, setFilters] = useState(() => {
    const currentMonth = getCurrentMonthRange();
    return {
    uid: '', // пользователь
      d_start_from: currentMonth.from, // дата начала с
      d_start_to: currentMonth.to, // дата начала по
    d_end_from: '', // дата окончания с
    d_end_to: '', // дата окончания по
    status: '', // статус
    };
  });

  const getLastMonthRange = () => {
    const now = new Date();
    const lastMonth = now.getMonth() - 1;
    const year = lastMonth < 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = lastMonth < 0 ? 11 : lastMonth;
    
    const startOfLastMonth = new Date(year, month, 1);
    const endOfLastMonth = new Date(year, month + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      from: formatDate(startOfLastMonth),
      to: formatDate(endOfLastMonth)
    };
  };
  
  // Состояние для сортировки
  const [sortField, setSortField] = useState('d_start');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Состояние для раскрытия фильтров
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Загрузка данных
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }
      
      // Формируем параметры запроса
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: limit.toString(),
        sort_field: sortField,
        sort_direction: sortDirection,
      });
      
      // Добавляем фильтры, если они заполнены
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const stringValue = typeof value === 'string' ? value.trim() : String(value);
          if (stringValue !== '') {
            params.append(key, stringValue);
          }
        }
      });
      
      const url = `${API_URL}${currentLang}/seo/sites/${site.id}/tasks?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setTasks(data.data.tasks || []);
        setTotalPages(data.data.pagination?.pages || 1);
        setTotalTasks(data.data.pagination?.total || 0);
      } else {
        throw new Error(data.message || 'Ошибка загрузки задач');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Загружаем данные при изменении параметров
  useEffect(() => {
    if (site?.id) {
      fetchTasks();
    }
  }, [currentPage, sortField, sortDirection, filters, site?.id]);
  
  // Автоматически раскрываем фильтры при наличии активных фильтров
  useEffect(() => {
    if (getActiveFiltersCount() > 0 && !filtersExpanded) {
      setFiltersExpanded(true);
    }
  }, [filters]);
  
  // Обработчики фильтров
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении фильтров
  };
  
  // Обработчик сортировки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };
  
  // Подсчет активных фильтров
  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => {
      if (value === null || value === undefined || value === '') return false;
      if (typeof value === 'string') return value.trim() !== '';
      return true; // Для числовых значений
    }).length;
  };
  
  // Очистка фильтров
  const clearFilters = () => {
    const currentMonth = getCurrentMonthRange();
    setFilters(prev => ({
      ...prev,
      uid: '',
      d_start_from: currentMonth.from,
      d_start_to: currentMonth.to,
      d_end_from: '',
      d_end_to: '',
      status: '',
    }));
    setCurrentPage(1);
  };
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return currentLang === 'ru' ? 'Не указана' : 'Not specified';
    return new Date(dateString).toLocaleDateString(currentLang === 'ru' ? 'ru-RU' : 'en-US');
  };
  
  // Получение иконки сортировки
  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };
  
  // Получение статуса задачи
  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return currentLang === 'ru' ? 'Ожидает' : 'Pending';
      case 1:
        return currentLang === 'ru' ? 'В работе' : 'In Progress';
      case 2:
        return currentLang === 'ru' ? 'Завершена' : 'Completed';
      case 3:
        return currentLang === 'ru' ? 'Отменена' : 'Cancelled';
      default:
        return currentLang === 'ru' ? 'Неизвестно' : 'Unknown';
    }
  };
  
  // Получение цвета статуса
  const getStatusColor = (status) => {
    switch (status) {
      case 0:
        return 'bg-yellow-100 text-yellow-800';
      case 1:
        return 'bg-blue-100 text-blue-800';
      case 2:
        return 'bg-green-100 text-green-800';
      case 3:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md dark:bg-navy-700 dark:text-white dark:border-white/10">
      {/* Заголовок */}
      <div className="flex justify-between items-center mb-6 p-4 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-center space-x-3">
          <h3 className="text-lg font-semibold">{currentLang === 'ru' ? 'Задачи' : 'Tasks'}</h3>
          {getActiveFiltersCount() > 0 && (
            <span className="px-2 py-1 text-xs font-bold bg-brand-500 text-white rounded-full">
              {getActiveFiltersCount()} {currentLang === 'ru' ? 'фильтр' : 'filter'}
              {getActiveFiltersCount() > 1 && (currentLang === 'ru' ? 'а' : 's')}
            </span>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            onClick={fetchTasks}
            disabled={loading}
            className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            {loading ? (currentLang === 'ru' ? 'Загрузка...' : 'Loading...') : (currentLang === 'ru' ? 'Обновить' : 'Refresh')}
          </button>
        </div>
      </div>

      {/* Фильтры */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10">
        {/* Заголовок фильтров */}
        <div 
          className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-600 rounded-lg p-2"
          onClick={() => setFiltersExpanded(!filtersExpanded)}
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h4 className="text-md font-medium">{currentLang === 'ru' ? 'Фильтры' : 'Filters'}</h4>
            </div>
            <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearFilters();
                  }}
                  className="px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md"
                >
                  {currentLang === 'ru' ? 'Очистить' : 'Clear'}
                </button>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${filtersExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          
          {/* Быстрые фильтры */}
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Фильтры по статусу */}
            <div className="flex flex-wrap gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '');
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                filters.status === '' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Все' : 'All'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '0');
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                filters.status === '0' 
                  ? 'bg-yellow-500 text-white border-yellow-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Ожидает' : 'Pending'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '1');
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                filters.status === '1' 
                  ? 'bg-blue-500 text-white border-blue-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'В работе' : 'In Progress'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '2');
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                filters.status === '2' 
                  ? 'bg-green-500 text-white border-green-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Завершена' : 'Completed'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleFilterChange('status', '3');
              }}
              className={`px-3 py-1 text-xs rounded-full border ${
                filters.status === '3' 
                  ? 'bg-red-500 text-white border-red-500' 
                  : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
              }`}
            >
              {currentLang === 'ru' ? 'Отменена' : 'Cancelled'}
            </button>
            </div>

            {/* Фильтры по датам */}
            <div className="flex flex-wrap gap-2 border-l border-gray-300 pl-2">
              {(() => {
                const currentMonth = getCurrentMonthRange();
                const lastMonth = getLastMonthRange();
                
                return (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('d_start_from', currentMonth.from);
                        handleFilterChange('d_start_to', currentMonth.to);
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.d_start_from === currentMonth.from && filters.d_start_to === currentMonth.to
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Этот месяц' : 'This month'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('d_start_from', lastMonth.from);
                        handleFilterChange('d_start_to', lastMonth.to);
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.d_start_from === lastMonth.from && filters.d_start_to === lastMonth.to
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Прошлый месяц' : 'Last month'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterChange('d_start_from', '');
                        handleFilterChange('d_start_to', '');
                      }}
                      className={`px-3 py-1 text-xs rounded-full border ${
                        filters.d_start_from === '' && filters.d_start_to === ''
                          ? 'bg-purple-500 text-white border-purple-500' 
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                    >
                      {currentLang === 'ru' ? 'Все даты' : 'All dates'}
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Раскрывающиеся фильтры */}
        {filtersExpanded && (
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Пользователь */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Пользователь' : 'User'}
                </label>
                <input
                  type="text"
                  value={filters.uid}
                  onChange={(e) => handleFilterChange('uid', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                  placeholder={currentLang === 'ru' ? 'ID пользователя' : 'User ID'}
                />
              </div>
              
              {/* Дата начала с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата начала с' : 'Start date from'}
                </label>
                <input
                  type="date"
                  value={filters.d_start_from}
                  onChange={(e) => handleFilterChange('d_start_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
              
              {/* Дата начала по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата начала по' : 'Start date to'}
                </label>
                <input
                  type="date"
                  value={filters.d_start_to}
                  onChange={(e) => handleFilterChange('d_start_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
              
              {/* Дата окончания с */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата окончания с' : 'End date from'}
                </label>
                <input
                  type="date"
                  value={filters.d_end_from}
                  onChange={(e) => handleFilterChange('d_end_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
              
              {/* Дата окончания по */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-white">
                  {currentLang === 'ru' ? 'Дата окончания по' : 'End date to'}
                </label>
                <input
                  type="date"
                  value={filters.d_end_to}
                  onChange={(e) => handleFilterChange('d_end_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Таблица */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-red-600 text-center">{error}</div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200 dark:divide-white/10">
              <thead className="bg-gray-50 dark:bg-navy-600">
                <tr>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('name')}
                  >
                    {currentLang === 'ru' ? 'Название' : 'Name'} {getSortIcon('name')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('status')}
                  >
                    {currentLang === 'ru' ? 'Статус' : 'Status'} {getSortIcon('status')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('pct')}
                  >
                    {currentLang === 'ru' ? 'Прогресс' : 'Progress'} {getSortIcon('pct')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('d_start')}
                  >
                    {currentLang === 'ru' ? 'Дата начала' : 'Start Date'} {getSortIcon('d_start')}
                  </th>
                  <th 
                    className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
                    onClick={() => handleSort('d_end')}
                  >
                    {currentLang === 'ru' ? 'Дата окончания' : 'End Date'} {getSortIcon('d_end')}
                  </th>
                  <th className="p-3 whitespace-nowrap text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-navy-600 dark:text-white">
                    {currentLang === 'ru' ? 'Доступ' : 'Access'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 dark:bg-navy-700 dark:divide-white/10">
                {tasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                    <td className="p-3 text-sm text-gray-900 dark:text-white max-w-xs">
                      <div className="font-medium">{task.name}</div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(task.status)}`}>
                        {getStatusLabel(task.status)}
                      </span>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div 
                            className="bg-brand-500 h-2 rounded-full" 
                            style={{ width: `${task.pct}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium">{task.pct}%</span>
                      </div>
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(task.d_start)}
                    </td>
                    <td className="p-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(task.d_end)}
                    </td>
                    <td className="p-3 text-sm text-gray-900 dark:text-white">
                      {task.access_users && task.access_users.length > 0 ? (
                        <div className="space-y-1">
                          {task.access_users.map((user, index) => (
                            <div key={user.id} className="flex items-center space-x-2">
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {user.name}
                              </span>
                              <span className={`px-1 py-0.5 text-xs font-medium rounded ${
                                user.type === 'author' ? 'bg-green-100 text-green-800' :
                                user.type === 'viewer' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {user.type === 'author' ? (currentLang === 'ru' ? 'Автор' : 'Author') :
                                 user.type === 'viewer' ? (currentLang === 'ru' ? 'Наблюдатель' : 'Viewer') :
                                 user.type}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Пагинация */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 dark:bg-navy-700 dark:border-white/10">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                  >
                    {currentLang === 'ru' ? 'Предыдущая' : 'Previous'}
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                  >
                    {currentLang === 'ru' ? 'Следующая' : 'Next'}
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-white">
                      {currentLang === 'ru' ? 'Показано' : 'Showing'} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {currentLang === 'ru' ? 'до' : 'to'} <span className="font-medium">{Math.min(currentPage * limit, totalTasks)}</span> {currentLang === 'ru' ? 'из' : 'of'} <span className="font-medium">{totalTasks}</span> {currentLang === 'ru' ? 'результатов' : 'results'}
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                      >
                        ←
                      </button>
                      
                      {/* Номера страниц */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-brand-50 border-brand-500 text-brand-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-navy-600 dark:border-white/10 dark:text-white'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 dark:bg-navy-600 dark:border-white/10 dark:text-white"
                      >
                        →
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Сообщение об отсутствии данных */}
      {!loading && tasks.length === 0 && !error && (
        <div className="text-center py-8 text-gray-500 dark:text-white">
          {currentLang === 'ru' ? 'Задачи не найдены' : 'Tasks not found'}
        </div>
      )}
    </div>
  );
};

export default SiteTasks; 